import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	getOrganisations,
	getSwitchableOrganisations,
	getOrganisationUsers,
	createOrganisation,
	updateOrganisation,
	type GetOrganisationsParams,
	type OrganisationDto,
	type OrganisationUserDto,
	type CreateOrganisationRequest,
	type UpdateOrganisationRequest,
} from '@/services/api/organisationsApi';

interface UseOrganisationsResult {
	organisations: OrganisationDto[];
	loading: boolean;
	// True while the previous params' rows are shown as placeholder for an in-flight fetch
	isPlaceholderData?: boolean;
	error: string | null;
	refetch: () => void;
}

/**
 * Hook for fetching the organisation list (platform organisation management screen).
 * With no params it returns the full, unfiltered list.
 */
export function useOrganisations(enabled = true, params: GetOrganisationsParams = {}): UseOrganisationsResult {
	const { data, isLoading, isError, error, refetch, isPlaceholderData } = useQuery({
		queryKey: ['organisations', params],
		queryFn: () => getOrganisations(params),
		enabled,
		placeholderData: (prev) => prev,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});

	return {
		organisations: Array.isArray(data) ? data : [],
		loading: isLoading,
		isPlaceholderData,
		error: isError ? (error as Error)?.message || 'Failed to fetch organisations' : null,
		refetch,
	};
}

/**
 * Hook for fetching the lightweight organisation list used by the GlobalAdmin org-switcher
 */
export function useSwitchableOrganisations(enabled = true): UseOrganisationsResult {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: ['organisations', 'switchable'],
		queryFn: () => getSwitchableOrganisations(),
		enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});

	return {
		organisations: Array.isArray(data) ? data : [],
		loading: isLoading,
		error: isError ? (error as Error)?.message || 'Failed to fetch organisations' : null,
		refetch,
	};
}

interface UseOrganisationUsersResult {
	users: OrganisationUserDto[];
	loading: boolean;
	error: string | null;
}

/**
 * Hook for fetching the users that belong to an organisation (provision-admin picker)
 */
export function useOrganisationUsers(id: number | null, enabled = true): UseOrganisationUsersResult {
	const { data, isLoading, isError, error } = useQuery({
		queryKey: ['organisations', id, 'users'],
		queryFn: () => getOrganisationUsers(id as number),
		enabled: enabled && id != null,
	});

	return {
		users: Array.isArray(data) ? data : [],
		loading: isLoading,
		error: isError ? (error as Error)?.message || 'Failed to fetch organisation users' : null,
	};
}

/**
 * Hook for creating an organisation
 */
export function useCreateOrganisation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateOrganisationRequest) => createOrganisation(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['organisations'] });
		},
	});
}

/**
 * Hook for updating an organisation (rename / toggle active)
 */
export function useUpdateOrganisation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: UpdateOrganisationRequest }) => updateOrganisation(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['organisations'] });
		},
	});
}
